import { createSlice } from '@reduxjs/toolkit';
import { deepCloneArray } from '../utils/array';

export const GlobalSlice = createSlice({
  name: 'global',
  initialState: {
    theme: {
      contrast: '162 156 134',
      // 'contrast-l': '220 215 196',
      main: '8 11 15',
      'main-d': '0 0 0',
      'primary-l': '6 104 110',
      primary: '3 55 59',
      'primary-d': '21 28 36',
      // 'secondary-l': '197 128 53',
      secondary: '142 76 40',
      'secondary-d': '74 37 17',
      red: '87 0 0',
      orange: '74 37 17',
      yellow: '71 55 11',
      lime: '54 72 20',
      green: '20 72 33',
      blue: '20 55 87',
      purple: '59 40 87',
      pink: '87 40 72',
    },
    isMobile: false,
    // Responsive helpers:
    // - isMobile: legacy name, now means "compact layout" (driven by width breakpoint, not orientation)
    // - isTouch: coarse pointer / touch-capable device (used to disable risky drag/resize interactions)
    // - isLandscape: screen orientation (only for orientation-specific layouts)
    isTouch: false,
    isLandscape: false,
    openModales: {},
    sidebar: { size: 400, open: true },
    drawer: { size: 400, open: null },
    currentTab: null,
    currentMobileTab: {},
    countProcessNum: {},
    navigation: { size: 200, open: false },
    history: [],
    picZoom: 4,
    listCampaignsId: [],
    boardViewports: {},
  },
  reducers: {
    setTheme: (state, action) => {
      state.theme = action.payload;
    },
    setThemeColors: (state, action) => {
      state.theme[action.payload.key] = action.payload.value;
    },
    setIsMobile: (state, action) => {
      state.isMobile = action.payload;
    },
    setIsTouch: (state, action) => {
      state.isTouch = action.payload;
    },
    setIsLandscape: (state, action) => {
      state.isLandscape = action.payload;
    },
    setOpenModales: (state, action) => {
      state.openModales[action.payload.id] = action.payload.open;
    },
    setSidebarSize: (state, action) => {
      state.sidebar.size = action.payload;
    },
    setSidebarOpen: (state, action) => {
      state.sidebar.open = action.payload;
    },
    setDrawerSize: (state, action) => {
      state.drawer.size = action.payload;
    },
    setDrawerOpen: (state, action) => {
      state.drawer.open = action.payload;
    },
    setCurrentTab: (state, action) => {
      state.currentTab = action.payload;
    },
    setCurrentMobileTab: (state, action) => {
      state.currentMobileTab[action.payload.itemId] = action.payload.tabId;
    },
    setCountProcessNum: (state, action) => {
      state.countProcessNum[action.payload.numId] = action.payload.count;
    },
    setNavigationOpen: (state, action) => {
      state.navigation.open = action.payload;
    },
    setNavigationSize: (state, action) => {
      state.navigation.size = action.payload;
    },
    setHistory: (state, action) => {
      const newItemId = action.payload;
      const clonedHistory = deepCloneArray(state.history);
      const setH = new Set([newItemId, ...clonedHistory]);
      state.history = Array.from(setH);
    },
    setPicZoom: (state, action) => {
      let newValue = state.picZoom;
      if (action.payload === 'more' && state.picZoom > 2) {
        if (state.picZoom === 6) {
          newValue = 4;
        } else {
          newValue = state.picZoom / 2;
        }
      }
      if (action.payload === 'less' && state.picZoom < 12) {
        if (state.picZoom === 4) {
          newValue = 6;
        } else {
          newValue = state.picZoom * 2;
        }
      }
      state.picZoom = newValue;
    },
    setListCampaignsId: (state, action) => {
      state.listCampaignsId = action.payload;
    },
    setBoardViewport: (state, action) => {
      const boardId = action.payload?.boardId;
      if (!boardId) {
        return;
      }
      state.boardViewports[boardId] = action.payload?.viewport || {
        x: 0,
        y: 0,
        zoom: 1,
      };
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setTheme,
  setThemeColors,
  setIsMobile,
  setIsTouch,
  setIsLandscape,
  setOpenModales,
  setSidebarSize,
  setSidebarOpen,
  setDrawerSize,
  setDrawerOpen,
  setCurrentTab,
  setCurrentMobileTab,
  setCountProcessNum,
  setNavigationOpen,
  setHistory,
  setPicZoom,
  setListCampaignsId,
  setNavigationSize,
  setBoardViewport,
} = GlobalSlice.actions;

export default GlobalSlice.reducer;
